import React, { useState } from "react";

/*  The Arc Diagnostic — Organisational AI Maturity
    Judgment in the Age of AI · Chapter 9 · Susan K.L. Yu
    Four dimensions (Adoption, Alignment, Autonomy, Governance), 12 questions,
    scored 1–5, total maps to Arc 1 (12–24), Arc 2 (25–42), Arc 3 (43–60).
    Run once per track (Workforce Augmentation / Enterprise Transformation at Scale). */

const C = {
  ink: "#16120C", panel: "#1E1913", panel2: "#241C11", line: "#3A3020",
  gold: "#C49A4A", goldb: "#E4C486", primary: "#EEE6D8", second: "#B8B0A0", muted: "#8A8069",
  serif: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
  sans: "'DM Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
};

const TRACKS = {
  workforce: {
    key: "workforce", name: "Workforce Augmentation",
    blurb: "The organisation-wide effort to empower every professional to work with AI. Culture, leadership and mindset do the heavy lifting.",
  },
  enterprise: {
    key: "enterprise", name: "Enterprise Transformation at Scale",
    blurb: "Building purpose-built AI agents and applications that connect across departmental boundaries.",
  },
};

const DIMS = {
  Adoption:  { color: "#4F79A8", desc: "How widely and habitually AI is used, trusted, and sustained." },
  Alignment: { color: "#3E8E7E", desc: "How well AI work is tied to business value, with stakeholders aligned." },
  Autonomy:  { color: "#C49A4A", desc: "How far the organisation has moved toward independent, connected, scalable AI." },
  Governance:{ color: "#B0555A", desc: "The oversight, accountability, and data governance around AI." },
};

const QUESTIONS = [
  { dim: "Adoption",  t: "AI use is a daily habit across most of the workforce for this track — not occasional or confined to enthusiasts." },
  { dim: "Adoption",  t: "People trust the AI tools enough to change how they actually work, not just experiment on the side." },
  { dim: "Adoption",  t: "Adoption is sustained and reinforced over time — not a one-off spike after a launch or training push." },
  { dim: "Alignment", t: "AI efforts on this track are clearly tied to business outcomes and strategic priorities, not technology for its own sake." },
  { dim: "Alignment", t: "The key stakeholders — business, technology, delivery and leadership — are aligned on what this track is trying to achieve." },
  { dim: "Alignment", t: "There is a shared, explicit definition of what success looks like for this track, and progress is measured against it." },
  { dim: "Autonomy",  t: "Work on this track has moved beyond assisted tasks toward AI systems or agents that operate with meaningful independence." },
  { dim: "Autonomy",  t: "AI capabilities connect across team, function or departmental boundaries rather than staying siloed." },
  { dim: "Autonomy",  t: "The organisation can build, deploy and scale new AI capability without starting from scratch each time." },
  { dim: "Governance",t: "There is a clear governance framework defining how AI is used responsibly, with named human accountability." },
  { dim: "Governance",t: "Data governance and security are adequate for the AI work being done on this track." },
  { dim: "Governance",t: "When an AI system produces an outcome, it is clear who is accountable and how the decision can be explained." },
];

const SCALE = [
  { v: 1, label: "Not at all" },
  { v: 2, label: "Rarely" },
  { v: 3, label: "Somewhat" },
  { v: 4, label: "Mostly" },
  { v: 5, label: "Fully" },
];

const ARCS = {
  1: { n: "Arc 1", name: "Adoption", tag: "Bringing AI into the organisation",
       guide: "You are in the work of bringing AI into the organisation. Focus on genuine, sustained adoption — culture, trust, and leaders modelling use — before reaching for autonomous systems. On the Workforce track especially, this is an ongoing discipline, not a project with a completion date." },
  2: { n: "Arc 2", name: "Optimisation", tag: "Optimising what adoption introduced",
       guide: "You are optimising what Arc 1 introduced. Tighten alignment to business value, connect AI across boundaries, and put governance in place before scaling autonomy. This is where early wins are turned into durable capability." },
  3: { n: "Arc 3", name: "Embedded judgment at scale", tag: "Governing autonomous systems at scale",
       guide: "You are governing autonomous systems at scale. Keep human accountability visible, sustain the culture that got you here, and ensure governance keeps pace with autonomy. The risk at this arc is complacency, not capability." },
};

function arcFor(total) { return total <= 24 ? 1 : total <= 42 ? 2 : 3; }

// Same backend as the giftbox / JPA. Results land in the unified Results tab, tagged source="arc-diagnostic".
const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzKF_m5xs2aalQFk4qIYnXMcRNGVq-MKJwgbVq5occAUFtGornrap8Tc6XcH6mKzWeF/exec";

function postResults(payload) {
  try {
    if (!APPS_SCRIPT_URL || APPS_SCRIPT_URL.indexOf("PASTE_") === 0) return;
    fetch(APPS_SCRIPT_URL, {
      method: "POST", mode: "no-cors",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
    }).catch(function () {});
  } catch (e) {}
}

// Injected once: print rules so "Save as PDF" produces a clean light report.
const PRINT_CSS = `
@media screen { #arc-print-report { display: none; } }
@media print {
  body * { visibility: hidden; }
  #arc-print-report, #arc-print-report * { visibility: visible; }
  #arc-print-report { display: block; position: absolute; left: 0; top: 0; width: 100%;
    background: #fff; color: #1f1a12; font-family: Georgia, 'Times New Roman', serif; padding: 24px 30px; }
  @page { size: A4; margin: 16mm; }
}`;

function PrintReport({ track, arc, total, dimScores, lowest, company }) {
  const g = "#8a6b2e", ink = "#17130c", line = "#e3dccb";
  return (
    <div id="arc-print-report">
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: g, fontWeight: "bold", fontFamily: "Arial, sans-serif" }}>
        Judgment Premium™ · The Arc Diagnostic
      </div>
      <h1 style={{ fontSize: 26, margin: "4px 0 2px", color: ink }}>{arc.n}: {arc.name}</h1>
      <div style={{ fontStyle: "italic", color: g, fontSize: 13 }}>{arc.tag}</div>
      <div style={{ height: 2, background: "#c49a4a", width: 46, margin: "10px 0 14px" }} />
      <table style={{ width: "100%", fontFamily: "Arial, sans-serif", fontSize: 11, marginBottom: 12 }}><tbody>
        <tr>
          <td style={{ padding: "2px 0" }}><b>Organisation:</b> {company || "—"}</td>
          <td style={{ padding: "2px 0", textAlign: "center" }}><b>Track:</b> {track}</td>
          <td style={{ padding: "2px 0", textAlign: "right" }}><b>Score:</b> {total} / 60 &nbsp;·&nbsp; {new Date().toLocaleDateString()}</td>
        </tr>
      </tbody></table>
      <div style={{ border: `1px solid ${line}`, borderLeft: "3px solid #c49a4a", borderRadius: 3, padding: "10px 13px", fontSize: 12.5, marginBottom: 16 }}>
        {arc.guide}
      </div>
      <h2 style={{ fontSize: 15, color: ink, borderBottom: `1px solid ${line}`, paddingBottom: 3, marginBottom: 8 }}>By dimension</h2>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}><tbody>
        {Object.keys(DIMS).map((d) => (
          <tr key={d} style={{ borderBottom: `1px solid ${line}` }}>
            <td style={{ padding: "6px 4px", fontWeight: "bold", width: "26%", color: ink }}>
              {d}{d === lowest ? "  · focus here" : ""}
            </td>
            <td style={{ padding: "6px 4px", width: "12%", color: g, fontWeight: "bold" }}>{dimScores[d]}/15</td>
            <td style={{ padding: "6px 4px", fontFamily: "Arial, sans-serif", color: "#4a4335" }}>{DIMS[d].desc}</td>
          </tr>
        ))}
      </tbody></table>
      <p style={{ fontSize: 10.5, color: "#8f866f", fontStyle: "italic", marginTop: 14, fontFamily: "Arial, sans-serif" }}>
        Different parts of an organisation may sit in different arcs at once. This is a practitioner-derived lens from Judgment in the Age of AI, for reflection and planning — not a validated instrument or a basis for ranking. © 2026 Susan K.L. Yu · Altschool Australia.
      </p>
    </div>
  );
}

export default function ArcDiagnostic() {
  const [screen, setScreen] = useState("welcome"); // welcome | assess | results
  const [track, setTrack] = useState(null);
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [company, setCompany] = useState("");
  const [respName, setRespName] = useState("");
  const [respEmail, setRespEmail] = useState("");
  const [showReq, setShowReq] = useState(false);

  const begin = (tk) => {
    if (!company.trim()) { setShowReq(true); return; }
    setTrack(tk); setAnswers({}); setI(0); setScreen("assess");
  };
  const choose = (v) => {
    const next = { ...answers, [i]: v };
    setAnswers(next);
    if (i < QUESTIONS.length - 1) setTimeout(() => setI(i + 1), 160);
    else {
      // final answer → compute and post the result once, then show results
      const total = Object.values(next).reduce((a, b) => a + b, 0);
      const ds = { Adoption: 0, Alignment: 0, Autonomy: 0, Governance: 0 };
      QUESTIONS.forEach((q, idx) => { ds[q.dim] += (next[idx] || 0); });
      const a = ARCS[arcFor(total)];
      postResults({
        source: "arc-diagnostic",
        id: "arc_" + Date.now(),
        org: company.trim(),                 // ← which company
        name: respName.trim(),
        email: respEmail.trim().toLowerCase(),
        mode: TRACKS[track].name,            // the track assessed
        total: total,                        // /60
        band: a.n + ": " + a.name,           // e.g. "Arc 2: Optimisation"
        dimScores: ds,                       // the diagnostic detail
        date: new Date().toISOString(),
      });
      setTimeout(() => setScreen("results"), 160);
    }
  };

  // shared styles
  const page = { minHeight: "100%", background: C.ink, color: C.primary, fontFamily: C.sans, padding: "0 0 64px" };
  const wrap = { maxWidth: 720, margin: "0 auto", padding: "0 22px" };
  const kicker = { textAlign: "center", color: C.gold, letterSpacing: 4, fontSize: 12, textTransform: "uppercase", fontWeight: 700, paddingTop: 40 };
  const h1 = { fontFamily: C.serif, fontWeight: 600, textAlign: "center", fontSize: 46, margin: "16px 0 8px", lineHeight: 1.05 };
  const goldBtn = { display: "block", width: "100%", cursor: "pointer", border: "none", borderRadius: 9, background: C.gold, color: "#231a0c", fontWeight: 700, fontSize: 16, padding: 16, fontFamily: C.sans, letterSpacing: .2 };

  /* ---------- WELCOME ---------- */
  if (screen === "welcome") {
    return (
      <div style={page}><div style={wrap}>
        <div style={kicker}>Judgment Premium™</div>
        <h1 style={h1}>The <em style={{ color: C.gold }}>Arc</em> Diagnostic</h1>
        <p style={{ textAlign: "center", color: C.second, fontSize: 18, fontStyle: "italic", maxWidth: 540, margin: "0 auto 6px" }}>
          Where is your organisation on its AI maturity journey?
        </p>
        <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: "24px 24px 26px", marginTop: 26 }}>
          <p style={{ margin: "0 0 14px", color: C.primary }}>
            Twelve questions across four dimensions — Adoption, Alignment, Autonomy and Governance — place your organisation in one of three arcs of AI maturity. It takes about five minutes.
          </p>
          <p style={{ margin: "0 0 18px", color: C.second, fontSize: 14.5 }}>
            Maturity is not one number. Run the diagnostic once for each track — most organisations sit at different arcs on each.
          </p>

          {(() => {
            const inp = { width: "100%", background: "#140e07", border: `1px solid ${showReq && !company.trim() ? "#c0785a" : C.line}`, borderRadius: 8, color: C.primary, fontSize: 15, padding: "12px 13px", fontFamily: C.sans, marginBottom: 10 };
            const lab = { display: "block", fontSize: 12, color: C.muted, margin: "0 0 5px", letterSpacing: .3 };
            return (
              <div style={{ marginBottom: 18 }}>
                <label style={lab}>Company / organisation *</label>
                <input style={inp} value={company} placeholder="Your organisation" onChange={(e) => { setCompany(e.target.value); if (showReq) setShowReq(false); }} />
                <div style={{ display: "flex", gap: 10 }}>
                  <input style={{ ...inp, marginBottom: 0 }} value={respName} placeholder="Your name (optional)" onChange={(e) => setRespName(e.target.value)} />
                  <input style={{ ...inp, marginBottom: 0 }} value={respEmail} placeholder="Email (optional)" onChange={(e) => setRespEmail(e.target.value)} />
                </div>
                {showReq && !company.trim() && (
                  <p style={{ color: "#d98a6a", fontSize: 12.5, margin: "8px 0 0" }}>Please enter your company before you begin.</p>
                )}
              </div>
            );
          })()}

          <p style={{ margin: "0 0 12px", color: C.second, fontSize: 14.5 }}>Choose a track to begin:</p>
          {Object.values(TRACKS).map((tk) => (
            <button key={tk.key} onClick={() => begin(tk.key)}
              style={{ display: "block", width: "100%", textAlign: "left", cursor: "pointer", background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 10, padding: "15px 16px", marginBottom: 12, color: C.primary }}>
              <div style={{ fontFamily: C.serif, fontSize: 21, fontWeight: 600, color: C.goldb }}>{tk.name}</div>
              <div style={{ fontSize: 13.5, color: C.second, marginTop: 3 }}>{tk.blurb}</div>
            </button>
          ))}
          <p style={{ fontSize: 12.5, color: C.muted, margin: "6px 0 0", textAlign: "center" }}>
            A practitioner diagnostic from <em>Judgment in the Age of AI</em> · not a validated instrument
          </p>
        </div>
      </div></div>
    );
  }

  /* ---------- ASSESSMENT ---------- */
  if (screen === "assess") {
    const q = QUESTIONS[i];
    const pct = Math.round((i / QUESTIONS.length) * 100);
    const accent = DIMS[q.dim].color;
    return (
      <div style={page}>
        <div style={{ background: C.panel, borderBottom: `1px solid ${C.line}`, padding: "12px 0" }}>
          <div style={{ ...wrap, display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 0 }}>
            <span style={{ color: C.gold, letterSpacing: 2, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>Arc Diagnostic</span>
            <span style={{ color: C.second, fontSize: 12 }}>{TRACKS[track].name}</span>
          </div>
        </div>
        <div style={{ height: 3, background: C.panel2 }}><div style={{ height: 3, width: `${pct}%`, background: C.gold, transition: "width .3s" }} /></div>
        <div style={{ ...wrap, paddingTop: 30 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <span style={{ fontSize: 12, letterSpacing: 1.5, color: C.muted, fontWeight: 700 }}>{`ITEM ${i + 1} OF ${QUESTIONS.length}`}</span>
            <span style={{ fontSize: 11.5, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700, color: accent }}>{q.dim}</span>
          </div>
          <p style={{ fontFamily: C.serif, fontSize: 25, lineHeight: 1.28, fontStyle: "italic", color: C.primary, margin: "0 0 26px" }}>{q.t}</p>
          {SCALE.map((o) => {
            const on = answers[i] === o.v;
            return (
              <button key={o.v} onClick={() => choose(o.v)}
                style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left", cursor: "pointer",
                  background: on ? "rgba(196,154,74,.14)" : C.panel, border: `1px solid ${on ? C.gold : C.line}`, borderRadius: 9, padding: "13px 15px", marginBottom: 9, color: C.primary }}>
                <span style={{ width: 26, height: 26, flexShrink: 0, borderRadius: "50%", border: `1px solid ${on ? C.gold : C.line}`, color: on ? "#231a0c" : C.second, background: on ? C.gold : "transparent", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>{o.v}</span>
                <span style={{ fontSize: 15 }}>{o.label}</span>
              </button>
            );
          })}
          {i > 0 && (
            <button onClick={() => setI(i - 1)} style={{ marginTop: 8, background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, fontFamily: C.sans }}>← Back</button>
          )}
        </div>
      </div>
    );
  }

  /* ---------- RESULTS ---------- */
  const total = Object.values(answers).reduce((a, b) => a + b, 0);
  const arc = ARCS[arcFor(total)];
  const dimScores = {};
  Object.keys(DIMS).forEach((d) => { dimScores[d] = 0; });
  QUESTIONS.forEach((q, idx) => { dimScores[q.dim] += (answers[idx] || 0); }); // 3–15 each
  const lowest = Object.keys(dimScores).reduce((a, b) => (dimScores[a] <= dimScores[b] ? a : b));

  return (
    <div style={page}>
      <style>{PRINT_CSS}</style>
      <PrintReport track={TRACKS[track].name} arc={arc} total={total} dimScores={dimScores} lowest={lowest} company={company} />
      <div style={wrap}>
      <div style={kicker}>Your result · {TRACKS[track].name}</div>
      <h1 style={{ ...h1, color: C.gold }}>{arc.n}: {arc.name}</h1>
      <p style={{ textAlign: "center", color: C.second, fontStyle: "italic", fontSize: 18, margin: "0 0 4px" }}>{arc.tag}</p>
      <p style={{ textAlign: "center", color: C.muted, fontSize: 13, marginBottom: 22 }}>{total} / 60</p>

      <div style={{ background: C.panel, border: `1px solid ${C.gold}`, borderRadius: 12, padding: "20px 22px", marginBottom: 18 }}>
        <p style={{ margin: 0, color: C.primary, fontSize: 15 }}>{arc.guide}</p>
      </div>

      <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 12, padding: "22px 22px 8px", marginBottom: 18 }}>
        <h2 style={{ fontFamily: C.serif, fontWeight: 600, fontSize: 22, margin: "0 0 14px" }}>By dimension</h2>
        {Object.keys(DIMS).map((d) => {
          const val = dimScores[d]; const p = (val / 15) * 100;
          return (
            <div key={d} style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                <span style={{ fontWeight: 600, color: C.primary }}>{d}{d === lowest && <span style={{ color: C.gold, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginLeft: 8 }}>FOCUS HERE</span>}</span>
                <span style={{ color: C.second, fontSize: 13 }}>{val}/15</span>
              </div>
              <div style={{ height: 8, background: C.panel2, borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: 8, width: `${p}%`, background: DIMS[d].color, borderRadius: 5 }} />
              </div>
              <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>{DIMS[d].desc}</div>
            </div>
          );
        })}
      </div>

      <div style={{ background: C.panel2, border: `1px solid ${C.line}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20, fontSize: 13.5, color: C.second }}>
        Different parts of your organisation may sit in different arcs at once — your digital function and your frontline are rarely in the same place. Treat this as a lens for one track, not a single verdict.
      </div>

      <button onClick={() => window.print()} style={{ ...goldBtn, marginBottom: 10 }}>
        Print / Save as PDF
      </button>
      <button onClick={() => begin(track === "workforce" ? "enterprise" : "workforce")}
        style={{ display: "block", width: "100%", cursor: "pointer", background: "transparent", border: `1px solid ${C.line}`, borderRadius: 9, color: C.gold, fontWeight: 700, fontSize: 14, padding: 14, fontFamily: C.sans, marginBottom: 10 }}>
        Assess the other track ({track === "workforce" ? "Enterprise Transformation" : "Workforce Augmentation"}) →
      </button>
      <button onClick={() => { setScreen("welcome"); setTrack(null); setAnswers({}); setI(0); }}
        style={{ display: "block", margin: "0 auto", cursor: "pointer", background: "none", border: "none", color: C.muted, fontSize: 13, fontFamily: C.sans }}>
        Start over
      </button>

      <p style={{ textAlign: "center", color: C.muted, fontSize: 11.5, marginTop: 22, lineHeight: 1.5 }}>
        The Arc Diagnostic is a practitioner-derived lens from <em>Judgment in the Age of AI</em>, for reflection and planning — not a validated instrument or a basis for ranking. © 2026 Susan K.L. Yu · Altschool Australia
      </p>
    </div></div>
  );
}
